/**
 * Base WebSocket Composable
 *
 * Provides reusable WebSocket connection management with:
 * - Automatic reconnection
 * - Connection state tracking
 * - Token authentication
 * - Multi-tenant schema support
 */

import { ref, onUnmounted } from 'vue';

export function useWebSocket() {
  const isConnected = ref(false);
  const isConnecting = ref(false);
  const error = ref(null);
  const lastMessage = ref(null);

  let ws = null;
  let reconnectTimeout = null;
  let reconnectAttempts = 0;
  // true depois de disconnect(): o onclose que o ws.close() dispara não deve reconectar
  let manualClose = false;
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  /**
   * Get WebSocket URL with proper protocol (ws:// or wss://)
   */
  const getWebSocketUrl = (endpoint, token, schema) => {
    // Get base URL from environment or use current host
    const baseUrl = process.env.VUE_APP_URL_BASE || '';

    // Extract host from base URL or use window.location
    let host;
    let protocol;

    if (baseUrl) {
      // Parse URL to extract host and determine protocol
      const urlObj = new URL(baseUrl);
      host = urlObj.host;
      // Use wss for https, ws for http
      protocol = urlObj.protocol === 'https:' ? 'wss:' : 'ws:';
    } else {
      // Fallback to current window location
      host = window.location.host;
      protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    }

    // Build WebSocket URL
    const wsUrl = `${protocol}//${host}${endpoint}?token=${token}&schema=${schema}`;

    console.log('🔌 WebSocket connecting to:', wsUrl);

    return wsUrl;
  };

  /**
   * Connect to WebSocket endpoint
   *
   * @param {string} endpoint - WebSocket endpoint (e.g., '/ws/location/123/')
   * @param {object} handlers - Event handlers { onMessage, onOpen, onClose, onError }
   * @param {boolean} autoReconnect - Enable automatic reconnection on disconnect
   */
  const connect = (endpoint, handlers = {}, autoReconnect = true) => {
    if (isConnecting.value || isConnected.value) {
      return;
    }

    // Get authentication token from localStorage
    const userDataString = localStorage.getItem('user');
    if (!userDataString) {
      error.value = 'No authentication token found. Please login.';
      return;
    }

    let token;
    try {
      const userData = JSON.parse(userDataString);
      token = userData.token;

      if (!token) {
        error.value = 'No token found in user data';
        return;
      }
    } catch (e) {
      error.value = 'Failed to parse user data';
      return;
    }

    // Get schema (empresa) from localStorage
    const schema = localStorage.getItem('selectedSchema') || 'public';

    isConnecting.value = true;
    error.value = null;
    manualClose = false;

    try {
      const wsUrl = getWebSocketUrl(endpoint, token, schema);
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        isConnected.value = true;
        isConnecting.value = false;
        reconnectAttempts = 0;
        error.value = null;

        if (handlers.onOpen) {
          handlers.onOpen();
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          lastMessage.value = data;

          if (handlers.onMessage) {
            handlers.onMessage(data);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onerror = (event) => {
        error.value = 'WebSocket connection error';

        if (handlers.onError) {
          handlers.onError(event);
        }
      };

      ws.onclose = (event) => {
        isConnected.value = false;
        isConnecting.value = false;

        if (manualClose) return;

        if (handlers.onClose) {
          handlers.onClose(event);
        }

        // Attempt to reconnect if enabled and not exceeded max attempts
        if (autoReconnect && reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;

          reconnectTimeout = setTimeout(() => {
            connect(endpoint, handlers, autoReconnect);
          }, reconnectDelay);
        } else if (reconnectAttempts >= maxReconnectAttempts) {
          error.value = 'Failed to reconnect after multiple attempts';
        }
      };

    } catch (e) {
      isConnecting.value = false;
      error.value = `Failed to create WebSocket: ${e.message}`;
      console.error('❌ Error creating WebSocket:', e);
    }
  };

  /**
   * Send message through WebSocket
   *
   * @param {object} data - Data to send (will be JSON stringified)
   */
  const send = (data) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      error.value = 'WebSocket is not connected';
      return false;
    }

    try {
      const message = JSON.stringify(data);
      ws.send(message);
      return true;
    } catch (e) {
      console.error('Error sending WebSocket message:', e);
      error.value = `Failed to send message: ${e.message}`;
      return false;
    }
  };

  /**
   * Disconnect from WebSocket
   */
  const disconnect = () => {
    manualClose = true;

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (ws) {
      ws.close();
      ws = null;
    }

    isConnected.value = false;
    isConnecting.value = false;
    reconnectAttempts = 0;
  };

  /**
   * Get current WebSocket ready state
   */
  const getReadyState = () => {
    if (!ws) return null;

    const states = {
      [WebSocket.CONNECTING]: 'CONNECTING',
      [WebSocket.OPEN]: 'OPEN',
      [WebSocket.CLOSING]: 'CLOSING',
      [WebSocket.CLOSED]: 'CLOSED'
    };

    return states[ws.readyState] || 'UNKNOWN';
  };

  // Cleanup on component unmount
  onUnmounted(() => {
    disconnect();
  });

  return {
    isConnected,
    isConnecting,
    error,
    lastMessage,
    connect,
    send,
    disconnect,
    getReadyState
  };
}
